import { useCallback, useEffect } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUITheme,
  Link as LinkIcon,
} from '@equip.health/ui';
import { useHistory, useLocation } from 'react-router-dom';

import { ApiCommand } from '~/lib/Api';
import {
  CalendarResource,
  CalendarResourceMapper,
} from '~/lib/constants/calendar.constants';
import { VITE_MAUD_BASE_URL } from '~/lib/constants/env';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import useCalendarElement from '~/lib/hooks/useCalendarElement';
import { getValueFromQueryParams } from '~/lib/utils';
import { Path } from '~/routes/constants';
import './calendar-sync.css';
import { useGetCalendarAccountQuery } from '~/store/services/user';

const { label } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'calendar-sync';

type SimpleErrorProps = {
  display: string;
  id: string;
};

const SimpleError = ({ display, id }: SimpleErrorProps) => (
  <ChakraText
    color="critical.base"
    id={`${TOP_LEVEL_ID}__${id}-error`}
    padding="8px"
  >
    {display}
  </ChakraText>
);

const CalendarSync = () => {
  const { elementToken, generateElementToken, setOptions } = useCalendarElement(
    CalendarResource.CALENDAR_SYNC,
  );
  const {
    data: calendarAccount,
    isLoading: isCalendarAccountLoading,
    isError: isCalendarAccountError,
    refetch: refetchCalendarAccount,
  } = useGetCalendarAccountQuery();

  const { profileConnected } = calendarAccount ?? {};

  const { search } = useLocation();
  const history = useHistory();

  const {
    error: authError,
    sendRequest: authRequest,
    statusCode: authRequestStatusCode,
  } = useApi<null>();

  const removeCodeFromUrl = useCallback((): void => {
    const queryParams = new URLSearchParams(search);
    if (queryParams.has('code')) queryParams.delete('code');
    if (queryParams.has('state')) queryParams.delete('state');
    history.replace({ search: queryParams.toString() });
  }, [history, search]);

  const exchangeAuthToken = useCallback((code: string): void => {
    authRequest({
      callback: removeCodeFromUrl,
      command: ApiCommand.POST,
      options: {
        code,
        redirectUri: `${VITE_MAUD_BASE_URL}${Path.CALENDAR_INTEGRATION}`,
      },
      url: urlConstants.calendar.auth,
    });
  }, []);

  useEffect(() => {
    if (authRequestStatusCode === 200) {
      refetchCalendarAccount();
    }
  }, [authRequestStatusCode]);

  useEffect(() => {
    const { options } = CalendarResourceMapper[CalendarResource.CALENDAR_SYNC];

    if (profileConnected) {
      // Generate an element token for a connected calendar account if one does not already exist
      if (!elementToken) {
        generateElementToken();
      }
      // Update element options for a connected calendar account if element token already exists
      else {
        setOptions({
          ...options,
          callback: (data: CalendarEvent) => {
            if (data?.notification?.type === 'profile_revoked') {
              refetchCalendarAccount();
            }
          },
          element_token: elementToken,
        });
      }
    }

    // Initialize calendar sync element without element token if calendar account is not connected
    else if (calendarAccount !== null) {
      setOptions(options);
    }
  }, [calendarAccount, profileConnected, elementToken]);

  /**
   * Check for code query param in URL. This happens when the user is redirected from Cronofy
   * after having granted permission to access their calendar.
   */
  useEffect(() => {
    if (!profileConnected && !isCalendarAccountLoading && search) {
      const code = getValueFromQueryParams(search, 'code');
      if (code) exchangeAuthToken(code);
    }
  }, [isCalendarAccountLoading, profileConnected, search]);

  return isCalendarAccountError ? (
    <SimpleError
      display="Error retrieving user account information"
      id="user-account"
    />
  ) : (
    <>
      <ChakraBox paddingRight="24px" position="relative">
        <div
          id={
            CalendarResourceMapper[CalendarResource.CALENDAR_SYNC].options
              .target_id
          }
        />
        {!profileConnected && (
          <ChakraFlex
            align="center"
            backgroundColor="primary.100"
            borderRadius="8px"
            bottom="16px"
            boxShadow="0px 2px 12px 2px #007F7932"
            height="30px"
            id={`${TOP_LEVEL_ID}__link-account-button`}
            justify="center"
            padding="7px 6px 6px"
            pointerEvents="none"
            position="absolute"
            right="48px"
            width="112px"
            zIndex={1}
          >
            <ChakraText
              {...label}
              color="white"
              fontWeight={700}
              id={`${TOP_LEVEL_ID}__link-account-button-label`}
              lineHeight="17px"
              marginRight="8px"
            >
              Link account
            </ChakraText>
            <LinkIcon fill="white" height="16px" width="16px" />
          </ChakraFlex>
        )}
      </ChakraBox>
      {!!authError && (
        <SimpleError display="Error linking account" id="link-account" />
      )}
    </>
  );
};

export default CalendarSync;
